import React, { useEffect, useState } from 'react';
import { Container, Paper, Box, Typography, TextField, Button, Alert, CircularProgress } from '@mui/material';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';

const Profile: React.FC = () => {
  const { user, loadUser, logout } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({
     firstName: '',
     lastName: '',
     name: '',
     email: '',
     department: '',
     position: '',
     superpowers: '',
     password: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    if (user) {
      const fullName = user.name || '';
      const parts = fullName.trim().split(/\s+/);
      const firstName = user.firstName || parts[0] || '';
      const lastName = user.lastName || parts.slice(1).join(' ');
      setForm({
        firstName,
        lastName,
        name: fullName,
        email: user.email || '',
        department: user.department || '',
        position: user.position || '',
        superpowers: (user as any).superpowers || '',
        password: '',
      });
    }
  }, [user]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);
    try {
      const payload: any = {
        firstName: form.firstName,
        lastName: form.lastName,
        name: `${form.firstName} ${form.lastName}`.trim() || form.name,
        email: form.email,
        department: form.department,
        position: form.position,
        superpowers: form.superpowers,
      };
      if (form.password.trim()) {
        payload.password = form.password;
      }
      await api.put('/users/me', payload);
      await loadUser();
      setSuccess('Profile updated successfully');
      setForm((p) => ({ ...p, password: '' }));
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="sm" sx={{ mt: 4, mb: 4 }}>
      <Paper elevation={3} sx={{ p: 4 }}>
        <Typography variant="h5" gutterBottom>
          My Profile
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Keep your basic details up to date and highlight your superpowers so your team knows what you love doing.
        </Typography>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

        <Box component="form" onSubmit={handleSubmit}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 3 }}>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                label="First Name"
                name="firstName"
                value={form.firstName}
                onChange={handleChange}
                fullWidth
              />
              <TextField
                label="Last Name"
                name="lastName"
                value={form.lastName}
                onChange={handleChange}
                fullWidth
              />
            </Box>
            <TextField label="Email" name="email" value={form.email} onChange={handleChange} type="email" fullWidth />
            <TextField label="Department" name="department" value={form.department} onChange={handleChange} fullWidth />
            <TextField label="Position" name="position" value={form.position} onChange={handleChange} fullWidth />
          </Box>

          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle1" sx={{ mb: 1 }}>
              Superpowers (soft skills)
            </Typography>
            <TextField
              name="superpowers"
              value={form.superpowers}
              onChange={handleChange}
              placeholder="e.g. Coaching, facilitating workshops, conflict resolution, stakeholder alignment"
              multiline
              minRows={3}
              fullWidth
            />
          </Box>

          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle1" sx={{ mb: 1 }}>
              Security
            </Typography>
            <TextField
              label="New Password (optional)"
              name="password"
              value={form.password}
              onChange={handleChange}
              type="password"
              fullWidth
            />
          </Box>

          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2 }}>
            <Button color="error" variant="outlined" onClick={() => { logout(); navigate('/login'); }}>
              Logout
            </Button>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button variant="outlined" onClick={() => window.history.back()}>Cancel</Button>
              <Button type="submit" variant="contained" disabled={loading}>
                {loading ? <CircularProgress size={22} /> : 'Save Changes'}
              </Button>
            </Box>
          </Box>
        </Box>
      </Paper>
    </Container>
  );
}
;

export default Profile;
